import React, { useRef } from 'react';
import Address from '../components/Address';
import BusinessHours from '../components/BusinessHours';
import EmployeeCard from '../components/EmployeeCard';
import {
  FetchEmployeeResult,
  FetchBranchInput,
  FetchEmployeeInput,
  FetchBranchResult
} from '../types';
import { fetchEmployee, fetchBranch } from '../api';
import styled from 'styled-components';
import css from '@styled-system/css';
import { usePromiseFunction, useScrollIntoViewOnMount } from '../hooks';
import { NoMatch } from '../components/NoMatch';
import { states } from '../configs/states';
import { H1, H2, H3, H4, H5, H6, Spinner, Button } from '@wco/toolkit';
import { Route } from 'react-router-dom';
import Link from '../components/Link';
import bankSEOConfig from '../configs';
import SEO from '../components/SEO';
import appConfig from '../config';

const EmployeeColumn = styled.div`
  ${css({
    width: ['100%', '100%', '33%'],
    marginTop: ['medium', 'medium', '0']
  })}

  position: relative;
  float: left;

  :last-child {
    float: right;
  }
`;

const EmployeeContent = styled.div`
  margin: 0;
  width: 100%;
`;

const Details = styled.div`
  margin-bottom: ${props => props.theme.space.large};
  display: block;
  padding-left: ${props => props.theme.space.small};
  padding-right: ${props => props.theme.space.small};
  float: left;
  margin-left: 0;

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    font-size: ${props => props.theme.fontSizes.text.medium};
    line-height: ${props => props.theme.lineHeights.xsmall};
    display: table;
    margin-right: ${props => props.theme.space.xxlarge};
  }

  li {
    overflow-wrap: break-word;
    word-wrap: break-word;
  }
`;
const Wrapper = styled.div<any>``;
const EmployeeHeader = styled.div`
  margin: 0 auto;
  width: 100%;
  margin-bottom: ${props => props.theme.space.xxxlarge};
`;

const EmployeeContainer = styled.div`
  padding: ${props => [props.theme.space.medium, props.theme.space.xlarge]};
  display: flex;
  flex-wrap: wrap;
`;

const Title = styled.span`
  display: block;
  color: #a6a6a6;
  text-transform: uppercase;
  font-weight: bold;
  font-size: ${props => props.theme.fontSizes.text.medium};
  text-rendering: optimizeLegibility;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  margin: ${props => props.theme.space.medium} 0
    ${props => props.theme.space.small};
`;

export function EmployeeDetails(props: any) {
  const { nextRouter } = props;
  const { empid, branchname } = props.match.params;
  const employeeInput: FetchEmployeeInput = {
    id: empid,
    branchName: branchname
  };

  const branchInput: FetchBranchInput = {
    branchName: branchname
  };
  const branch = usePromiseFunction(fetchBranch, branchInput.branchName);
  const employee = usePromiseFunction(
    fetchEmployee,
    employeeInput.id,
    employeeInput.branchName
  );

  const isPending =
    employee.state === states.pending || branch.state === states.pending;
  const isError =
    employee.state === states.error || branch.state === states.error;

  if (isPending) {
    return <Spinner />;
  } else if (isError) {
    if (appConfig.notFoundPage) {
      return (
        <>
          <Spinner />
          <Route
            path="*"
            component={() => {
              window.location.href = `${appConfig.notFoundPage}`;
              return null;
            }}
          />
        </>
      );
    } else {
      return (
        <NoMatch
          header={'Medarbejder findes ikke'}
          message={'Den medarbejder du har søgt efter findes ikke'}
        />
      );
    }
  } else {
    return (
      <>
        {employee.state === states.ok && branch.state === states.ok ? (
          <EmployeeDetailContainer
            branch={branch.data}
            employee={employee.data}
            employeeHeadlineSize={props.employeeHeadlineSize}
            employeeBranchHeadlineSize={props.employeeBranchHeadlineSize}
            nextRouter={nextRouter}
            branchUrl={branchname}
          />
        ) : (
          <Spinner />
        )}
      </>
    );
  }
}

type EmployeeDetailContainerProps = {
  branch: FetchBranchResult;
  employee: FetchEmployeeResult;
  employeeHeadlineSize: string;
  employeeBranchHeadlineSize: string;
  nextRouter?: any;
  branchUrl: string;
};

function EmployeeDetailContainer(props: EmployeeDetailContainerProps) {
  const { branch, employee, nextRouter, branchUrl } = props;
  const employeeDetails = employee;
  const branchDetails = branch;
  const branchNameForSEO = branchDetails.branchName.replace(/(^\w|\s\w)/g, m =>
    m.toUpperCase()
  );

  const resultsRef = useRef();
  useScrollIntoViewOnMount(resultsRef);

  const TitleComp = () => {
    const { employee } = props;
    return (
      <>
        {employee.title
          ? employee.firstName + ' ' + employee.lastName + ', ' + employee.title
          : employee.firstName + ' ' + employee.lastName}
      </>
    );
  };

  const renderTitle = () => {
    if (props.employeeHeadlineSize === 'h1') {
      return (
        <H1>
          <TitleComp />
        </H1>
      );
    } else if (props.employeeHeadlineSize === 'h2') {
      return (
        <H2>
          <TitleComp />
        </H2>
      );
    } else if (props.employeeHeadlineSize === 'h3') {
      return (
        <H3>
          <TitleComp />
        </H3>
      );
    } else if (props.employeeHeadlineSize === 'h4') {
      return (
        <H4>
          <TitleComp />
        </H4>
      );
    } else if (props.employeeHeadlineSize === 'h5') {
      return (
        <H5>
          <TitleComp />
        </H5>
      );
    } else {
      return (
        <H6>
          <TitleComp />
        </H6>
      );
    }
  };

  const renderBranchName = () => {
    if (props.employeeBranchHeadlineSize === 'h1') {
      return <H1>{branch.branchName}</H1>;
    } else if (props.employeeBranchHeadlineSize === 'h2') {
      return <H2>{branch.branchName}</H2>;
    } else if (props.employeeBranchHeadlineSize === 'h3') {
      return <H3>{branch.branchName}</H3>;
    } else if (props.employeeBranchHeadlineSize === 'h4') {
      return <H4>{branch.branchName}</H4>;
    } else if (props.employeeBranchHeadlineSize === 'h5') {
      return <H5>{branch.branchName}</H5>;
    } else {
      return <H6>{branch.branchName}</H6>;
    }
  };

  return (
    <>
      <SEO
        title={`${employeeDetails.firstName} ${employeeDetails.lastName} - ${bankSEOConfig.fullBankName} ${branchNameForSEO}`}
        description={`${employeeDetails.firstName} ${employeeDetails.lastName} er ${employeeDetails.title} i ${branchNameForSEO}. 
          Kontakt ${employeeDetails.firstName} på telefon ${employeeDetails.phone} eller e-mail: ${employeeDetails.email}`}
      />
      <Wrapper ref={resultsRef}>
        <EmployeeContainer data-testid="employee-container">
          <EmployeeHeader>
            <Title>Medarbejder</Title> {renderTitle()}
          </EmployeeHeader>
          <EmployeeContent>
            <EmployeeColumn>
              <EmployeeCard
                employee={employeeDetails}
                url={branchUrl}
              ></EmployeeCard>
            </EmployeeColumn>
            <EmployeeColumn>
              {renderBranchName()}
              <Details>
                <H5>Kontaktoplysninger</H5>
                <Address {...branchDetails} />
              </Details>
              <Details>
                <H5>Åbningstider</H5>
                <BusinessHours
                  opening={branchDetails.openingHours}
                ></BusinessHours>
              </Details>
            </EmployeeColumn>
          </EmployeeContent>
          <Link to={`/${branchDetails.friendlyName}`} nextRouter={nextRouter}>
            <Button>Tilbage til oversigt</Button>
          </Link>
        </EmployeeContainer>
      </Wrapper>
    </>
  );
}
